/* ManageInstructionsRenderer.js */

"use strict";
const _ = require("lodash");
const EntityGrid = require("@gluenetworks/entity_grid_gl").Grid;
const Registrations = require("../../../../../../../RegistrationNamespaces");

class ManageInstructionsRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("manage-instructions-view-gl");
        this._rootElem.component = this.model;
        this._logger = this._model._logger;

        this._instructionsSection = undefined;
        this._emptyGridContainer = document.createElement("div");
        this._onUserSorted = this._onUserSorted.bind(this);
        this._instructionEntityGrid = undefined;
        this._defaultViewActive = false;

        this._instructionsSectionContainer = document.createElement("div");
        this._instructionsSectionContainer.classList.add("instruction-grid-section");

        // Lines number display container
        this._linesNumberContainer = document.createElement("div");
        this._linesNumberContainer.classList.add("lines-number-container");

        this._registerListeners();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        // Add event listeners here if needed
    }

    _unregisterListeners() {
        // Remove event listeners here if needed
    }

    _registerGridListeners() {
        this._model.pagingFeed.on(
            "RefreshView",
            (this._onFeedRefreshView = this._onFeedRefreshView.bind(this))
        );
        this._instructionEntityGrid.on("UserSorted", this._onUserSorted);
    }

    _unregisterGridListeners() {
        this._model.pagingFeed.removeListener("RefreshView", this._onFeedRefreshView);
        if (this._instructionEntityGrid) {
            this._instructionEntityGrid.removeListener("UserSorted", this._onUserSorted);
        }
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    _onUserSorted(eventData) {
        this._model._onUserSorted(eventData);
    }

    async shutdown() {
        this._unregisterListeners();
        this._unregisterGridListeners();

        if (this._defaultViewActive === true) {
            await this._hideDefaultMessageDisplay().catch(() => {});
        }

        const promArr = [
            _.isUndefined(this._instructionEntityGrid)
                ? Promise.resolve()
                : this._instructionEntityGrid.shutdown(),
        ];

        return Promise.all(promArr).then(
            () => {
                // Clean up grid reference
                this._instructionEntityGrid = undefined;

                // Clean up DOM elements
                if (this._instructionsSectionContainer) {
                    this._instructionsSectionContainer.remove();
                    this._instructionsSectionContainer = undefined;
                }
                if (this._linesNumberContainer) {
                    this._linesNumberContainer.remove();
                    this._linesNumberContainer = undefined;
                }
                if (this._emptyGridContainer) {
                    this._emptyGridContainer.remove();
                    this._emptyGridContainer = undefined;
                }

                // Clean up state
                this._defaultViewActive = undefined;
                this._initialEntityCount = undefined;
                this._instructionsSection = undefined;

                // Clean up bound methods
                this._onUserSorted = undefined;
                this._onFeedRefreshView = undefined;

                // Clean up references
                this._logger = undefined;
                this._model = undefined;

                // Clean up root element last
                if (this._rootElem) {
                    this._rootElem.remove();
                    this._rootElem = undefined;
                }
            },
            (err) => {
                if (this._logger) {
                    this._logger.warn(err, "ManageInstructionsRenderer.shutdown() error");
                }
            }
        );
    }

    _onFeedRefreshView() {
        const totalEntities = this._model.pagingFeed.totalEntities;
        if (totalEntities === 0) {
            if (this._defaultViewActive === false) {
                this._showDefaultMessageDisplay();
            }
        } else {
            if (this._defaultViewActive === true) {
                this._hideDefaultMessageDisplay();
            }
        }
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     */
    async render() {
        await this._model.whenModelIsReady();

        this._rootElem.appendChild(this._instructionsSectionContainer);
        this._rootElem.appendChild(this._linesNumberContainer);
        this.rootElement.appendChild(this._emptyGridContainer);
        this._initialEntityCount = this._model.pagingFeed.totalEntities;

        this._renderInstructions();
        this._registerListeners();

        return;
    }

    _renderInstructions() {
        this._instructionsSectionContainer.innerHTML = "";

        this._instructionEntityGrid = new EntityGrid({
            gridKey: this.model._key,
            logger: this._logger,
            singleSelect: true,
            columns: this._model.columnConfig,
            pagingFeed: this._model.pagingFeed,
            showHeader: true,
            showLinesNumber: this._linesNumberContainer,
            modalUserFeedback: this._model._modalUserFeedback,
            userGridData: this._model._userGridData,
        });
        // create entity grid and render it within this._instructionsSectionContainer
        this._instructionsSectionContainer.appendChild(
            this._instructionEntityGrid.renderer.rootElement
        );
        this._registerGridListeners();

        this._onFeedRefreshView();
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    _showDefaultMessageDisplay() {
        const message = {
            message: "Add an Instruction",
            icon: "configdrift.add_device",
            action: Registrations.PLATFORM_EXPLORER_ACTION_ADD_INSTRUCTIONS,
        };
        return this._model._gameEditor
            .showDefaultMessageView(message, this._instructionsSectionContainer)
            .then(() => {
                this._defaultViewActive = true;
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "ManageInstructionsRenderer._showDefaultMessageDisplay error"
                );
            });
    }
    _hideDefaultMessageDisplay() {
        return this._model._gameEditor
            .hideDefaultMessageView()
            .then(() => {
                this._defaultViewActive = false;
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "ManageInstructionsRenderer._hideDefaultMessageDisplay error"
                );
                throw err;
            });
    }
}

module.exports = ManageInstructionsRenderer;
